import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { RiderTracking } from '@/components/RiderTracking';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Package, MapPin, Clock, CheckCircle, Navigation as NavIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const RiderDashboard = ({ user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyOrders();
  }, []);

  const fetchMyOrders = async () => {
    try {
      const response = await axios.get('/orders', {
        params: { rider_id: user.id, status: 'IN_CUSTODY,EN_ROUTE' }
      });
      setOrders(response.data);
      
      // Si hay una orden en tracking activo, establecerla como activa
      const trackingOrder = response.data.find(o => o.tracking_active);
      if (trackingOrder) {
        setActiveOrder(trackingOrder);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      await axios.post(`/orders/${orderId}/accept`);
      toast.success('Pedido aceptado');
      fetchMyOrders();
    } catch (error) {
      toast.error('Error al aceptar pedido: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStartDelivery = (order) => {
    setActiveOrder(order);
  };

  const handleCompleteDelivery = async (orderId) => {
    try {
      await axios.post(`/orders/${orderId}/deliver`);
      toast.success('Entrega completada');
      setActiveOrder(null);
      fetchMyOrders();
    } catch (error) {
      toast.error('Error al completar entrega: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'READY_FOR_PICKUP': { label: 'Listo para recoger', color: 'bg-blue-500' },
      'IN_CUSTODY': { label: 'En custodia', color: 'bg-yellow-500' },
      'EN_ROUTE': { label: 'En ruta', color: 'bg-purple-500' },
      'DELIVERED': { label: 'Entregado', color: 'bg-green-500' }
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} title="Mis Entregas">
        <div className="flex justify-center items-center min-h-screen">
          <p>Cargando...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} title="Mis Entregas">
      <div className="p-6 bg-[#EAE0D5] min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold mb-6 text-[#224049]">
            <Package className="inline mr-2" />
            Mis Entregas
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Panel izquierdo - Tracking activo */}
            <div className="lg:col-span-1">
              {activeOrder ? (
                <div className="space-y-4">
                  <Card className="bg-white">
                    <CardHeader>
                      <CardTitle>Pedido Activo</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600">Cliente:</p>
                        <p className="font-medium">{activeOrder.customer?.name}</p>
                        <p className="text-sm text-gray-500">{activeOrder.customer?.phone}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600">Dirección:</p>
                        <p className="text-sm">{activeOrder.delivery_address?.line1}</p>
                        <p className="text-xs text-gray-500">
                          {activeOrder.delivery_address?.city}, {activeOrder.delivery_address?.postal_code}
                        </p>
                      </div>

                      {activeOrder.route_order !== null && (
                        <div className="flex items-center gap-2 text-sm">
                          <NavIcon className="w-4 h-4 text-[#C38154]" />
                          <span>Parada #{activeOrder.route_order + 1} en tu ruta</span>
                        </div>
                      )}

                      <Button
                        onClick={() => handleCompleteDelivery(activeOrder.id)}
                        className="w-full bg-[#0F7A5B] hover:bg-[#0d6549]"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Marcar como Entregado
                      </Button>
                    </CardContent>
                  </Card>

                  <RiderTracking 
                    orderId={activeOrder.id}
                    onTrackingStart={() => fetchMyOrders()}
                    onTrackingStop={() => fetchMyOrders()}
                  />
                </div>
              ) : (
                <Card className="bg-white">
                  <CardContent className="pt-6">
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600">No hay entregas activas</p>
                      <p className="text-sm text-gray-500 mt-2">
                        Acepta un pedido para iniciar el tracking
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Panel derecho - Lista de pedidos */}
            <div className="lg:col-span-2">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle>Pedidos Disponibles</CardTitle>
                </CardHeader>
                <CardContent>
                  {orders.length === 0 ? (
                    <div className="text-center py-12">
                      <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-600">No hay pedidos asignados</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {orders
                        .sort((a, b) => (a.route_order || 999) - (b.route_order || 999))
                        .map((order, index) => (
                        <Card key={order.id} className="border border-gray-200">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  {getStatusBadge(order.status)}
                                  {order.mode === 'EXPRESS' && (
                                    <Badge className="bg-[#C38154]">Express 60'</Badge>
                                  )}
                                  {order.route_order !== null && (
                                    <Badge variant="outline">
                                      Parada #{order.route_order + 1}
                                    </Badge>
                                  )}
                                </div>

                                <p className="font-medium text-lg mb-1">
                                  {order.customer?.name}
                                </p>
                                <p className="text-sm text-gray-600">{order.customer?.phone}</p>

                                <div className="mt-3 space-y-1">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-[#C38154] mt-0.5" />
                                    <div className="text-sm">
                                      <p>{order.delivery_address?.line1}</p>
                                      <p className="text-gray-500">
                                        {order.delivery_address?.city}, {order.delivery_address?.postal_code}
                                      </p>
                                    </div>
                                  </div>

                                  {order.route_distance_meters && (
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                      <NavIcon className="w-4 h-4" />
                                      <span>{(order.route_distance_meters / 1000).toFixed(1)} km</span>
                                      <Clock className="w-4 h-4 ml-2" />
                                      <span>{Math.round(order.route_duration_seconds / 60)} min</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="ml-4">
                                {order.status === 'READY_FOR_PICKUP' && (
                                  <Button
                                    onClick={() => handleAcceptOrder(order.id)}
                                    size="sm"
                                    className="bg-[#224049]"
                                  >
                                    Aceptar
                                  </Button>
                                )}
                                {(order.status === 'IN_CUSTODY' || order.status === 'EN_ROUTE') && !activeOrder && (
                                  <Button
                                    onClick={() => handleStartDelivery(order)}
                                    size="sm"
                                    className="bg-[#0F7A5B]"
                                  >
                                    Iniciar
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RiderDashboard;
